import React, { createContext, useReducer, useContext, useEffect, useRef, useCallback } from "react";
import { useUserRole, useUserId } from "../util_functions/helpers";
import axios from "axios";
import config from "../config/config";

import {
  connectMessageSocket,
  disconnectMessageSocket,
  handleMessage,
} from "../websockets/ChatMessageSocketService";

import {
  sortAndFilter,
  upsertMessage,
  computeUnreadCounts,
} from "../util_functions/helpers";

export const THREAD_LOADED = 'THREAD_LOADED';
export const SET_MESSAGES = 'SET_MESSAGES';
export const SELECT_MESSAGE = 'SELECT_MESSAGE';
export const MESSAGE_RECEIVED = 'MESSAGE_RECEIVED';
export const MESSAGE_SENT = 'MESSAGE_SENT';
export const THREAD_UPDATED = 'THREAD_UPDATED';
export const MARK_MESSAGE_READ = 'MARK_MESSAGE_READ';
export const SET_JOB = 'SET_JOB';
export const SET_UNREAD = 'SET_UNREAD';
export const SET_THREAD_UNREAD = 'SET_THREAD_UNREAD';
export const SET_TOTAL_UNREAD = 'SET_TOTAL_UNREAD';
export const MARK_MESSAGES_READ_BULK = 'MARK_MESSAGES_READ_BULK';
export const MARK_THREAD_READ = 'MARK_THREAD_READ';


const ChatContext = createContext();

const initialState = {
  messages: [],
  selectedMessageId: null,
  client: {},
  unreadCounts: {},
};

function reducer(state, action) {
  switch (action.type) {
    case THREAD_LOADED:
      return {
        ...state,
        messages: state.messages.map(t => t.id === action.payload.id
          ? {
            ...action.payload,
            fullMessages: Array.isArray(action.payload.fullMessages)
              ? action.payload.fullMessages
              : t.fullMessages
          }
          : t
        ),
      };

    case SET_MESSAGES:
      return {
        ...state,
        messages: action.payload.map((m) => ({
          ...m,
          fullMessages: Array.isArray(m.fullMessages) ? m.fullMessages : [],
        })),
      };

    case SELECT_MESSAGE:
      return {
        ...state,
        selectedMessageId: action.payload,
      };

    case MESSAGE_RECEIVED:
    case MESSAGE_SENT:
      return {
        ...state,
        messages: upsertMessage(state.messages, {
          threadId: action.payload.threadId,
          fullMessage: action.payload.message,
        }),
      };

    case THREAD_UPDATED:
      return {
        ...state,
        messages: state.messages.map(thread =>
          thread.id === action.payload.id
            ? {
              ...action.payload,
              fullMessages: Array.isArray(action.payload.fullMessages)
                ? action.payload.fullMessages
                : thread.fullMessages,
            }
            : thread
        ),
      };

    case MARK_MESSAGE_READ:
      return {
        ...state,
        messages: state.messages.map(thread => {
          if (thread.id !== action.payload.threadId) return thread;
          return {
            ...thread,
            fullMessages: thread.fullMessages.map(msg =>
              action.payload.messageId == null
                ? { ...msg, read: true }
                : (msg.messageId === action.payload.messageId ? { ...msg, read: true } : msg)
            ),
          };
        }),
      };

    case SET_THREAD_UNREAD:
      return { ...state, threadCounts: action.payload }
    case SET_TOTAL_UNREAD:
      return { ...state, totalUnread: action.payload }


    case SET_JOB:
      return {
        ...state,
        jobDetails: action.payload.job,
        client: action.payload.client,
      };

    case SET_UNREAD:
      return {
        ...state,
        unreadCounts: action.payload,
      };

    case MARK_MESSAGES_READ_BULK:
      return {
        ...state,
        messages: state.messages.map(thread =>
          thread.id !== action.payload.threadId
            ? thread
            : {
              ...thread,
              fullMessages: thread.fullMessages.map(m =>
                action.payload.messageIds.includes(m.messageId)
                  ? { ...m, read: true }
                  : m
              ),
            }
        ),
      };


    case MARK_THREAD_READ:
      return {
        ...state,
        messages: state.messages.map(thread =>
          thread.id !== action.payload.threadId
            ? thread
            : {
              ...thread,
              fullMessages: thread.fullMessages.map(m => ({ ...m, read: true })),
            }
        ),
      };

    default:
      return state;
  }
}

export function PSChatProvider({ children }) {
  const userRole = useUserRole();
  const userId = useUserId();
  const userIdRef = useRef(userId);
  const userRoleRef = useRef(userRole);

  const pendingReadIdsRef = useRef(new Map());
  const flushTimerRef = useRef(null);

  console.log('[PSChatProvider] rendered; currentUserId =', useUserId());
  console.log('[PSChatProvider] rendered; currentUserRole =', useUserRole());
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchMessages = useCallback(async (page = 0, size = 20) => {
    try {
      const url = `${config.API_BASE_URL}/v1/messages/${userRole}/${userId}`;
      const { data: pageData } = await axios.get(url, { params: { page, size } });
      const payload = pageData.content || pageData;
      console.log(
        `[PSChatProvider] received ${Array.isArray(payload) ? payload.length : payload.content.length} messages`
      );
      dispatch({ type: SET_MESSAGES, payload });
    } catch (e) {
      console.error("Failed to fetch messages", e);
    }
  }, [userRole, userId]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${config.API_BASE_URL}/v1/messages/unread/count/${userId}`,
        { params: { userType: userRole } }
      );
      dispatch({ type: SET_UNREAD, payload: data });
    } catch (e) {
      console.error("Failed to fetch unread count", e);
    }
  }, [userId, userRole]);

  const sendMessage = useCallback(async (chatMessage) => {
    try {
      const { data } = await axios.post(
        `${config.API_BASE_URL}/v1/messages`,
        chatMessage
      );
      dispatch({ type: MESSAGE_SENT, payload: { threadId: data.id, message: data.fullMessages.slice(-1)[0] } });
    } catch (e) {
      console.error("Failed to send message", e);
    }
  }, []);

  const insertFullMessage = useCallback(async (threadId, fullMessage) => {
    try {
      const { data } = await axios.post(
        `${config.API_BASE_URL}/v1/messages/addChatMessage/${threadId}`,
        fullMessage
      );
      dispatch({ type: THREAD_UPDATED, payload: data });
    } catch (e) {
      console.error("Failed to insert full message", e);
    }
  }, []);

  const markMessageRead = useCallback(async (threadId, messageId) => {
    console.log(`[PSChat] markMessageRead → thread=${threadId}, message=${messageId}`);
    try {
      await axios.patch(
        `${config.API_BASE_URL}/v1/messages/read/${threadId}/message/${messageId}`
      );
      dispatch({ type: MARK_MESSAGE_READ, payload: { threadId, messageId } });
    } catch (err) {
      console.error("[PSChat] Failed to mark message as read", err);
    }
  }, []);

  const queuePendingRead = useCallback((threadId, ids) => {
    if (!pendingReadIdsRef.current.has(threadId)) {
      pendingReadIdsRef.current.set(threadId, new Set());
    }
    const set = pendingReadIdsRef.current.get(threadId);
    ids.forEach(id => set.add(id));
  }, []);

  const flushPendingReads = useCallback(async () => {
    if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null; }

    const entries = Array.from(pendingReadIdsRef.current.entries());
    if (!entries.length) return;

    entries.forEach(([threadId, set]) => {
      const ids = Array.from(set);
      dispatch({ type: 'MARK_MESSAGES_READ_BULK', payload: { threadId, messageIds: ids } });
    });

    await Promise.all(entries.map(async ([threadId, set]) => {
      const ids = Array.from(set);
      try {
        await axios.patch(`${config.API_BASE_URL}/v1/messages/markReadBulk/${threadId}`, ids);
        pendingReadIdsRef.current.delete(threadId);
      } catch (e) {
        console.error('markReadBulk failed:', e);
      }
    }));

    await fetchUnreadCount?.();
  }, [fetchUnreadCount]);

  const scheduleFlushPendingReads = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushPendingReads();
    }, 300);
  }, [flushPendingReads]);

  const markReadOptimistic = useCallback((threadId, ids) => {
    dispatch({ type: 'MARK_MESSAGES_READ_BULK', payload: { threadId, messageIds: ids } });
    queuePendingRead(threadId, ids);
    scheduleFlushPendingReads();
  }, [queuePendingRead, scheduleFlushPendingReads]);


  const markReadBulk = useCallback(async (threadId, messageIds = []) => {
    try {
      const ids = Array.from(new Set((messageIds || []).filter(Boolean)));

      dispatch({ type: MARK_THREAD_READ, payload: { threadId } });
      await axios.patch(
        `${config.API_BASE_URL}/v1/messages/markReadBulk/${threadId}`,
        ids
      );
    } catch (e) {
      console.error("Failed to mark read bulk", e);
    }
  }, [fetchUnreadCount]);

  const fetchThreadById = useCallback(async (threadId, userId) => {
    const url = `${config.API_BASE_URL}/v1/messages/threads/${threadId}`;
    const { data } = await axios.get(url, { params: { userId } });
    dispatch({ type: THREAD_UPDATED, payload: data });
    return data;
  }, []);

  const pinThread = useCallback(async (threadId, pinned, userId) => {
    const { data } = await axios.patch(
      `${config.API_BASE_URL}/v1/messages/${threadId}/pin`,
      { pinned, userId }
    );
    dispatch({ type: THREAD_UPDATED, payload: data });
    return data;
  }, []);

  const archiveThread = useCallback(async (threadId, archived, userId) => {
    const { data } = await axios.patch(
      `${config.API_BASE_URL}/v1/messages/${threadId}/archive`,
      { archived, userId }
    );
    dispatch({ type: THREAD_UPDATED, payload: data });
    return data;
  }, []);

  const softDeleteThread = useCallback(async (threadId, userId) => {
    dispatch({
      type: THREAD_UPDATED,
      payload: {
        id: threadId,
        threadDeleted: true,
        threadDeletedAt: Date.now(),
        threadDeletedBy: userId,
        latestMessage: "<em>[thread deleted by " + userId + "]</em>",
        fullMessages: undefined,
      }
    });

    const { data } = await axios.patch(
      `${config.API_BASE_URL}/v1/messages/${threadId}/delete`,
      { userId }
    );

    dispatch({ type: THREAD_UPDATED, payload: data });
    return data;
  }, []);


  const pinMessage = useCallback(async (threadId, messageId, pinned, userId) => {
    const { data } = await axios.patch(
      `${config.API_BASE_URL}/v1/messages/${threadId}/${messageId}/pin`,
      { pinned, userId }
    );
    dispatch({ type: THREAD_UPDATED, payload: data });
    return data;
  }, []);

  const editMessage = useCallback(async (threadId, messageId, {
    text,
    editReason,
    userId,
    userRole
  }) => {
    const { data } = await axios.patch(
      `${config.API_BASE_URL}/v1/messages/${threadId}/${messageId}`,
      { text, editReason, userId, userRole }
    );
    dispatch({ type: THREAD_UPDATED, payload: data });
    return data;
  }, []);


  const softDeleteMessage = useCallback(async (threadId, messageId, userId) => {
    const { data } = await axios.patch(
      `${config.API_BASE_URL}/v1/messages/${threadId}/${messageId}/delete`,
      { userId }
    );
    dispatch({ type: THREAD_UPDATED, payload: data });
    return data;
  }, []);


  const blockCalls = useCallback(
    async (threadId, value, reason, userIdParam) => {
      const { data } = await axios.patch(
        `${config.API_BASE_URL}/v1/messages/${threadId}/block-calls`,
        { value, reason, userId: userIdParam }
      );
      dispatch({ type: THREAD_UPDATED, payload: data });
      return data;
    },
    []
  );

  const blockUser = useCallback(
    async (threadId, value, reason, userIdParam) => {
      const { data } = await axios.patch(
        `${config.API_BASE_URL}/v1/messages/${threadId}/block-user`,
        { value, reason, userId: userIdParam }
      );
      dispatch({ type: THREAD_UPDATED, payload: data });
      return data;
    },
    []
  );

  const reportThread = useCallback(async (threadId, reason, details, userId) => {
    const { data } = await axios.patch(
      `${config.API_BASE_URL}/v1/messages/thread/${threadId}/report`,
      { reason, details, userId }
    );
    dispatch({ type: THREAD_UPDATED, payload: data });
    return data;
  }, []);


  const resolveReport = useCallback(
    async (threadId, status, userIdParam) => {
      const { data } = await axios.patch(
        `${config.API_BASE_URL}/v1/messages/${threadId}/report/resolve`,
        null,
        {
          params: { status },
          headers: { "X-User-Id": userIdParam },
        }
      );
      dispatch({ type: THREAD_UPDATED, payload: data });
      return data;
    },
    []
  );

  const setThreadNotifications = useCallback(
    async (threadId, enabled, userId) => {
    console.log("Thread notifications invoked")
      const { data } = await axios.patch(
        `${config.API_BASE_URL}/v1/messages/${threadId}/notifications`,
        { enabled, userId }
      );

      dispatch({ type: THREAD_UPDATED, payload: data });
      return data;
    },
    []
  );

  const toggleThreadNotifications = useCallback(
    async (threadId, userId) => {
      const thread = state.messages.find((t) => t.id === threadId);
      const current = !!thread?.notificationsEnabled;
      const next = !current;

      const updated = await setThreadNotifications(threadId, next, userId);
      return updated;
    },
    [state.messages, setThreadNotifications]
  );


  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    userRoleRef.current = userRole;
  }, [userRole]);

  useEffect(() => {
    const counts = computeUnreadCounts(state.messages, userRole);
    console.log("[PSChatProvider] Updating unread counts →", counts);
    dispatch({ type: "SET_UNREAD", payload: counts });
  }, [state.messages, userRole]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    const socket = new WebSocket(config.MESSAGE_SOCKET_URL);

    socket.onopen = () => {
      console.log("[PSChatProvider] WebSocket connected");
    };

    socket.onmessage = async (event) => {
      console.log(`[PSChatProvider] About to fetch messages for userId : ${userIdRef.current} and user role : ${userRoleRef.current}`);
      fetchMessages();
    };

    socket.onerror = (err) => {
      console.error("[Provider] WebSocket error:", err);
    };

    return () => socket.close();
  }, [fetchMessages]);


  return (
    <ChatContext.Provider value={{
      state,
      dispatch,
      fetchMessages,
      fetchUnreadCount,
      sendMessage,
      insertFullMessage,
      markMessageRead,
      markReadBulk,
      markReadOptimistic,
      flushPendingReads,
      fetchThreadById,
      pinThread,
      archiveThread,
      softDeleteThread,
      pinMessage,
      editMessage,
      softDeleteMessage,
      blockCalls,
      blockUser,
      reportThread,
      resolveReport,
      setThreadNotifications,
      toggleThreadNotifications,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export const usePsChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return ctx;
};
